/**
 * Pilot → Paid auto-conversion status card.
 *
 * Shows activation score (0-100) + each signal's contribution + manual trigger
 * to generate + email the Pilot Conversion Proposal PDF.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { TrendingUp, FileDown, CheckCircle2, X, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SIGNAL_META = [
  { key: 'pipeline_connected', label: 'Pipeline connected', points: 20 },
  { key: 'week1_report_sent', label: 'Week-1 ROI report sent', points: 20 },
  { key: 'has_30d_data', label: '≥30d of events captured', points: 15 },
  { key: 'loss_makers', label: '≥3 loss-making features identified', points: 10, valueFromActivation: (a) => a.loss_maker_count >= 3 },
  { key: 'ai_cost', label: 'AI cost > $100 in last 30d', points: 10, valueFromActivation: (a) => (a.total_ai_cost_30d_usd || 0) > 100 },
  { key: 'conversion_roi_page_views', label: '≥3 visits to /admin/conversion-roi', points: 15, valueFromActivation: (a) => (a.conversion_roi_page_views || 0) >= 3 },
  { key: 'anomalies', label: '≥1 anomaly opened', points: 10, valueFromActivation: (a) => (a.anomaly_count || 0) >= 1 },
];

export default function PilotToPaidCard() {
  const [status, setStatus] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/pilot-to-paid/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setStatus(await r.json());
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const trigger = async () => {
    setTriggering(true);
    setTriggerResult(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/pilot-to-paid/trigger`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setTriggerResult(r.ok ? { ok: true, ...d } : { ok: false, error: d.detail || `HTTP ${r.status}` });
      refresh();
    } finally { setTriggering(false); }
  };

  if (!status) return <Card className="bg-slate-950 border-emerald-500/20 animate-pulse" data-testid="p2p-loading"><CardContent className="p-6 h-32" /></Card>;

  const a = status.activation || {};
  const score = a.total_score || 0;
  const threshold = a.threshold || 50;
  const pct = Math.min(100, Math.round((score / 100) * 100));
  const meetsThreshold = a.meets_threshold;
  const sent = status.dispatch?.sent_at;

  return (
    <Card className="bg-slate-950 border-emerald-500/30" data-testid="p2p-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            Pilot → Paid auto-conversion
            <Badge className={`${meetsThreshold ? 'bg-emerald-500/20 text-emerald-200 border-emerald-400/40' : 'bg-amber-500/20 text-amber-200 border-amber-400/40'} border text-[9px] font-mono tracking-widest ml-1`}>
              {sent ? 'PROPOSAL SENT' : meetsThreshold ? 'ELIGIBLE' : 'BUILDING'}
            </Badge>
          </CardTitle>
          <div className="text-right">
            <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest">Activation score</div>
            <div className="text-2xl font-bold tabular-nums" data-testid="p2p-score">
              <span className={meetsThreshold ? 'text-emerald-400' : 'text-amber-300'}>{score}</span>
              <span className="text-slate-300">/100</span>
            </div>
            <div className="text-[10px] text-slate-300">Threshold: {threshold}</div>
          </div>
        </div>
        <p className="text-sm text-slate-200 mt-2">
          7 days after your Week-1 ROI report, CARTIE auto-generates a tailored conversion proposal: top loss-makers, custom pricing recommendation, payback math, and a CTA. Scores ≥{threshold}/100 trigger auto-dispatch.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Progress bar */}
        <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden">
          <div
            className={`h-full transition-all ${meetsThreshold ? 'bg-emerald-500 bg-gradient-to-r from-emerald-500 to-emerald-400' : 'bg-amber-500 bg-gradient-to-r from-amber-500 to-amber-400'}`}
            style={{ width: `${pct}%` }}
          />
        </div>

        {/* Signal breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2">
          {SIGNAL_META.map(s => {
            const ok = s.valueFromActivation ? s.valueFromActivation(a) : Boolean(a[s.key]);
            return (
              <div key={s.key} className={`rounded-md border px-3 py-2 flex items-center gap-2 ${ok ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-slate-900 border-slate-700/40'}`} data-testid={`p2p-signal-${s.key}`}>
                {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-300 shrink-0" /> : <X className="w-4 h-4 text-slate-300 shrink-0" />}
                <span className={`text-xs ${ok ? 'text-white' : 'text-slate-300'} flex-1`}>{s.label}</span>
                <span className={`text-[10px] font-mono font-bold ${ok ? 'text-emerald-300' : 'text-slate-300'}`}>+{s.points}</span>
              </div>
            );
          })}
        </div>

        {status.dispatch?.sent_at && (
          <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-3 text-xs space-y-0.5">
            <div className="text-emerald-300 font-bold flex items-center gap-1.5"><Mail className="w-3 h-3" /> Proposal sent</div>
            <div className="text-slate-200">To: <code className="text-emerald-300">{status.dispatch.to_email}</code></div>
            <div className="text-slate-200">On: <span className="font-mono">{new Date(status.dispatch.sent_at).toLocaleString()}</span></div>
            {status.dispatch.total_annual_savings_usd && (
              <div className="text-slate-200">Pitched savings: <span className="text-emerald-300 font-bold">${status.dispatch.total_annual_savings_usd.toLocaleString()}/yr</span></div>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button onClick={trigger} disabled={triggering} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="p2p-trigger-btn">
            <FileDown className="w-4 h-4 mr-1.5" />
            {triggering ? 'Generating PDF…' : sent ? 'Resend proposal' : 'Send proposal now'}
          </Button>
          {triggerResult && (
            triggerResult.ok ? (
              <div className="text-xs text-emerald-300">
                ✓ Score {triggerResult.activation_score}/100 · ${triggerResult.total_annual_savings_usd?.toFixed(0)} pitched · PDF {(triggerResult.pdf_size_bytes / 1024).toFixed(1)}kb · {triggerResult.send_result?.sent ? 'emailed' : `skipped (${triggerResult.send_result?.reason})`}
              </div>
            ) : (
              <div className="text-xs text-rose-300">{triggerResult.error}</div>
            )
          )}
        </div>
      </CardContent>
    </Card>
  );
}
